import UserRole from '../../models/enums/user-roles';
import AgreementDetails from '../../models/general/agreements/agreement-details';
import ListAgreement from '../../models/general/agreements/list-agreement';
import CurrentUser, { defaultUserState } from '../../models/general/current-user';
import CustomLocalization from '../../models/general/custom-localizations/custom-localization';
import FrontendParams from '../../models/general/frontend-params';
import NotificationSubscription from '../../models/general/notifications/notification-trigger';
import ReportDataSet from '../../models/general/notifications/report-data-set';
import UpdateVersion from '../../models/general/versions/update-version';
import VersionSet from '../../models/general/versions/version-set';
import TorizonPackage from '../../models/general/versions/torizon-package';
import TorizonVersionSet from '../../models/general/versions/torizon-version-set';
import AssignableRobot from '../../models/general/versions/assignable-robot';
import HttpError from '@common/interfaces/api/http-error';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import RestSet, { defaultRestSet } from '@common/interfaces/api/rest-set';
import ValidationResult from '@common/interfaces/api/validation-result';

export const accessTokenStorageName = 'sphere_accessToken';

export interface UserRestSet extends RestSet {
  postSetPasswordStatus: HttpFetchStatus;
  postSetPasswordError: HttpError | ValidationResult[] | null;
  postResetPasswordEmailStatus: HttpFetchStatus;
  postResetPasswordEmailError: HttpError | ValidationResult[] | null;
  postSubscribeRobotStatus: HttpFetchStatus;
  postSubscribeRobotError: HttpError | null;
  postSubscribeOrganizationStatus: HttpFetchStatus;
  postSubscribeOrganizationError: HttpError | null;

  postChangePasswordStatus: HttpFetchStatus;
  postChangePasswordError: HttpError | ValidationResult[] | null;
}

export interface AgreementRestSet extends RestSet {
  postUploadFileStatus: HttpFetchStatus;
  postUploadFileError: HttpError | ValidationResult[] | null;
  deleteUploadFileStatus: HttpFetchStatus;
  deleteUploadFileError: HttpError | null;
  postAcceptAgreementStatus: HttpFetchStatus;
  postAcceptAgreementError: HttpError | ValidationResult[] | null;
  getAgreementStatus: HttpFetchStatus;
  getAgreementError: HttpError | ValidationResult[] | null;
}

export interface NotificationRestSet extends RestSet {
  getReportDataStatus: HttpFetchStatus;
  getReportDataError: HttpError | null;
}

export interface TwoFactorRestSet {
  verifyTwoFactorPostStatus: HttpFetchStatus;
  verifyTwoFactorPostError: HttpError | ValidationResult[] | null;
  confirmTwoFactorSetupPostStatus: HttpFetchStatus;
  confirmTwoFactorSetupPostError: HttpError | ValidationResult[] | null;
  cancelTwoFactorSetupPostStatus: HttpFetchStatus;
  cancelTwoFactorSetupPostError: HttpError | ValidationResult[] | null;
  disableTwoFactorPostStatus: HttpFetchStatus;
  disableTwoFactorPostError: HttpError | ValidationResult[] | null;
  resetUserTwoFactorPostStatus: HttpFetchStatus;
  resetUserTwoFactorPostError: HttpError | ValidationResult[] | null;
}

interface GeneralState {
  user: {
    data: CurrentUser;
    rest: UserRestSet;
  };
  accessToken: {
    localStorageChecked: boolean;
    token: string | null;
  };
  twoFactor: {
    pending: {
      token?: string;
      setupRequired: boolean;
    } | null;
    rest: TwoFactorRestSet;
  };
  customUserRole: UserRole | null;
  agreements: {
    data: ListAgreement[] | null;
    rest: AgreementRestSet;
    selectedId: string | null;
    currentDetails: AgreementDetails | null;
    uploadProgress: number | null;
  };
  customLocalizations: {
    data: CustomLocalization[] | null;
    rest: RestSet;
    selectedId: string | null;
  };
  notifications: {
    data: NotificationSubscription[] | null;
    reportDataSets: ReportDataSet[] | null;
    rest: NotificationRestSet;
    selectedNotification: Partial<NotificationSubscription> | null;
  };
  versions: {
    // Debian
    sets: {
      data: VersionSet[] | null;
      rest: RestSet;
    };
    versions: {
      data: UpdateVersion[] | null;
      rest: RestSet;
    };
    // Torizon
    torizonPackages: {
      data: TorizonPackage[] | null;
      rest: RestSet;
    };
    torizonSets: {
      data: TorizonVersionSet[] | null;
      rest: RestSet;
    };
    assignableRobots: {
      data: AssignableRobot[] | null;
      rest: RestSet;
    };
  };
  userInviteData: {
    rest: {
      getStatus: HttpFetchStatus;
      getError: HttpError | null;
      postStatus: HttpFetchStatus;
      postError: HttpError | ValidationResult[] | null;
    };
    organizationId: string | null;
  };
  frontendParams: {
    data: FrontendParams | null;
    getStatus: HttpFetchStatus;
    getError: HttpError | null;
  };

  isAdvancedModeEnabled: boolean;
}

export const defaultState = {
  data: null,
  rest: defaultRestSet,
};

export const getInitialGeneralState = (): GeneralState => {
  return {
    user: {
      data: defaultUserState,
      rest: {
        ...defaultRestSet,
        postSetPasswordStatus: HttpFetchStatus.Idle,
        postSetPasswordError: null,
        postResetPasswordEmailStatus: HttpFetchStatus.Idle,
        postResetPasswordEmailError: null,
        postSubscribeRobotStatus: HttpFetchStatus.Idle,
        postSubscribeRobotError: null,
        postSubscribeOrganizationStatus: HttpFetchStatus.Idle,
        postSubscribeOrganizationError: null,
        postChangePasswordStatus: HttpFetchStatus.Idle,
        postChangePasswordError: null,
      },
    },
    accessToken: { localStorageChecked: false, token: null },
    twoFactor: {
      pending: null,
      rest: {
        verifyTwoFactorPostStatus: HttpFetchStatus.Idle,
        verifyTwoFactorPostError: null,
        confirmTwoFactorSetupPostStatus: HttpFetchStatus.Idle,
        confirmTwoFactorSetupPostError: null,
        cancelTwoFactorSetupPostStatus: HttpFetchStatus.Idle,
        cancelTwoFactorSetupPostError: null,
        disableTwoFactorPostStatus: HttpFetchStatus.Idle,
        disableTwoFactorPostError: null,
        resetUserTwoFactorPostStatus: HttpFetchStatus.Idle,
        resetUserTwoFactorPostError: null,
      },
    },
    customUserRole: null,
    agreements: {
      data: null,
      selectedId: null,
      uploadProgress: null,
      currentDetails: null,
      rest: {
        ...defaultRestSet,
        deleteUploadFileError: null,
        deleteUploadFileStatus: HttpFetchStatus.Idle,
        postAcceptAgreementError: null,
        postAcceptAgreementStatus: HttpFetchStatus.Idle,
        postUploadFileError: null,
        postUploadFileStatus: HttpFetchStatus.Idle,
        getAgreementError: null,
        getAgreementStatus: HttpFetchStatus.Idle,
      },
    },
    customLocalizations: {
      data: null,
      rest: defaultRestSet,
      selectedId: null,
    },
    notifications: {
      data: null,
      reportDataSets: null,
      rest: {
        ...defaultRestSet,
        getReportDataStatus: HttpFetchStatus.Idle,
        getReportDataError: null,
      },
      selectedNotification: null,
    },
    versions: {
      sets: {
        data: null,
        rest: defaultRestSet,
      },
      versions: {
        data: null,
        rest: defaultRestSet,
      },
      torizonPackages: {
        data: null,
        rest: { ...defaultRestSet },
      },
      torizonSets: {
        data: null,
        rest: { ...defaultRestSet },
      },
      assignableRobots: {
        data: null,
        rest: { ...defaultRestSet },
      },
    },
    userInviteData: {
      rest: {
        getStatus: HttpFetchStatus.Idle,
        getError: null,
        postStatus: HttpFetchStatus.Idle,
        postError: null,
      },
      organizationId: null,
    },
    frontendParams: {
      data: null,
      getStatus: HttpFetchStatus.Idle,
      getError: null,
    },
    isAdvancedModeEnabled: false,
  };
};
export type { GeneralState };
